import React, { useEffect, useState, useCallback } from 'react';
import { SkeletonLoader } from '@hpstellar/core';
import { Typography, RadioCard, PriceBlockUs } from '@hpstellar/core/visId';
import { CheckmarkCircleFilled } from '@hpstellar/veneer-icons';

import useCartState, { useCartActions } from '../../../hooks/useCartState';
import useDeviceInfo from '../../../hooks/useDeviceInfo';
import LoaderOverlay from '../loader-overlay';
import { Helpers } from '../../../core/src/helpers';

import './delivery-options.less';

const defaultDeliveryOptions = new Array(3).fill({ loading: true });

const PlaceHolderOptions = () => {
    return <SkeletonLoader className="option" />;
};

export default ({ title = 'Delivery options', csrActions }) => {
    const { cartInfo, cartViewIsLoading, emptyCart } = useCartState();
    const { id: orderId, cartView, disableCartAction } = cartInfo || {};
    const { lastUpdateDate } = cartView || {};
    const { device } = useDeviceInfo(['device'], 'default');
    const [deliveryOptions, setDeliveryOptions] = useState();
    const { getDeliveryOptions, updateShipMethod, getDeliveryOptionsMessage, getAdditionalDeliveryMessages } =
        useCartActions();
    const [deliverOptionMessage, setDeliverOptionMessage] = useState();
    const [additionalDeliveryMessages, setAdditionalDeliveryMessages] = useState();
    const [updatingDelivery, setUpdatingDelivery] = useState(false);
    const { selectedShipModeId, updatedFromService, orderAllowedShipMethods, heavyWeightCart } = deliveryOptions || {};
    const { setPalatizeShipping } = csrActions || {};
    const slim = device === 'mobile';

    const fetchDeliveryOptions = useCallback(() => {
        if (!orderId) return;
        getDeliveryOptions()
            .then(data => {
                setDeliveryOptions(data);
            })
            .catch(error => {
                console.error('delivery options error', error);
            });
        getDeliveryOptionsMessage().then(data => {
            setDeliverOptionMessage(data);
        });
    }, [orderId]);

    const handleShipMethodChange = useCallback(
        shipMethodId => () => {
            if (shipMethodId) {
                setTimeout(() => {
                    setUpdatingDelivery(true);
                    setDeliveryOptions({ ...deliveryOptions, selectedShipModeId: shipMethodId });
                    updateShipMethod(shipMethodId)
                        .then(data => {
                            setUpdatingDelivery(false);
                        })
                        .catch(e => {
                            setUpdatingDelivery(false);
                            //if there is an error, revert the selected ship method
                            fetchDeliveryOptions();
                        });
                }, 0);
            }
        },
        [orderId, selectedShipModeId],
    );
    useEffect(() => {
        if (heavyWeightCart && !additionalDeliveryMessages && !cartViewIsLoading) {
            getAdditionalDeliveryMessages().then(data => {
                setAdditionalDeliveryMessages(data);
            });
        }
        if (!heavyWeightCart && additionalDeliveryMessages) {
            setAdditionalDeliveryMessages(null);
        }
        if (setPalatizeShipping && heavyWeightCart) {
            setPalatizeShipping(true);
        }
    }, [heavyWeightCart, setPalatizeShipping]);

    useEffect(() => {
        if (!orderId || !lastUpdateDate) return;
        fetchDeliveryOptions();
    }, [orderId, lastUpdateDate]);

    const deliveryOptionList = orderAllowedShipMethods?.length > 0 ? orderAllowedShipMethods : defaultDeliveryOptions;

    if (updatedFromService && orderAllowedShipMethods?.length === 0) {
        return null;
    }

    return (
        <div
            className={`delivery-options${emptyCart ? ' empty-cart' : ''}${cartViewIsLoading ? ' loading' : ''}${slim ? ' slim' : ''}`}
        >
            <Typography variant="h4" tag="h4" className="title-text">
                {title}
            </Typography>
            <div className={disableCartAction ? 'disable-action' : ''}>
                <LoaderOverlay isLoading={updatingDelivery}>
                    <div className="option-group">
                        {deliveryOptionList.map((item, index) => {
                            const { shipCode, shipMethodDesc, shippingCharge, loading, shipMethodId } = item;
                            const price =
                                shippingCharge <= 0 ? 'Free' : Helpers.formatCurrency(shippingCharge, undefined, true);
                            const checked = selectedShipModeId * 1 === shipMethodId;
                            if (loading) {
                                return <PlaceHolderOptions key={index} />;
                            }

                            return (
                                <RadioCard
                                    className="option"
                                    key={shipCode}
                                    inputProps={{
                                        defaultChecked: checked,
                                        name: 'deliveryOption',
                                    }}
                                    onChange={handleShipMethodChange(shipMethodId)}
                                >
                                    {!slim && (
                                        <Typography
                                            id={`deliveryOption${index}`}
                                            weight={700}
                                            className="option-header"
                                        >
                                            {shipCode}
                                            {checked && <CheckmarkCircleFilled className="checkmark" size={16} />}
                                        </Typography>
                                    )}
                                    <Typography
                                        weight={slim ? 700 : undefined}
                                        className={slim ? 'short-description' : ''}
                                    >
                                        {shipMethodDesc}
                                        {slim && (
                                            <>
                                                <Typography tag="span">{` | ${shipCode}`}</Typography>
                                                {checked && <CheckmarkCircleFilled className="checkmark" size={16} />}
                                            </>
                                        )}
                                    </Typography>
                                    <PriceBlockUs finalPrice={price} finalPriceTypographyVariant="caption" />
                                </RadioCard>
                            );
                        })}
                    </div>
                </LoaderOverlay>
                {deliverOptionMessage && (
                    <Typography
                        className="cust-html"
                        variant="caption"
                        dangerouslySetInnerHTML={Helpers.createMarkup(deliverOptionMessage)}
                    />
                )}
                {additionalDeliveryMessages?.length > 0 &&
                    additionalDeliveryMessages.map((message, index) => (
                        <Typography
                            key={index}
                            className="cust-html cust-html-additional"
                            variant="caption"
                            dangerouslySetInnerHTML={Helpers.createMarkup(message)}
                        />
                    ))}
            </div>
        </div>
    );
};
